/**
 * Analytics event taxonomy (PIX-551).
 *
 * Every event name and property key the app emits lives here. The point is
 * drift: across ~70 call sites, one string literal or one typo silently
 * splits a funnel in two, and nothing throws - the damage only surfaces weeks
 * later as a dashboard that quietly stopped counting some of its traffic.
 * PIX-570 adds a regression check that fails the build on an inline name.
 *
 * Names follow PostHog's convention: snake_case, object first, past-tense
 * verb last (`checkout_plan_selected`). Names prefixed with `$` are PostHog
 * built-ins and must keep their exact spelling.
 */

export const EVENTS = Object.freeze({
  // PostHog built-in. Captured manually (capture_pageview is off) so the
  // route NAME survives - SPA autocapture only ever sees the URL.
  PAGE_VIEWED: '$pageview',

  // Signup wizard (PIX-555). SIGNUP_COMPLETED marks account creation at
  // verification, not the trial-vs-pay choice that follows it (PIX-557).
  SIGNUP_STEP_VIEWED: 'signup_step_viewed',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_ABANDONED: 'signup_abandoned',
  SIGNUP_VERIFICATION_SENT: 'signup_verification_sent',
  SIGNUP_VERIFICATION_RESENT: 'signup_verification_resent',
  SIGNUP_VERIFICATION_FAILED: 'signup_verification_failed',
  // A server-side rejection that throws the student back to an earlier step.
  // Plausible silent killer: they land on step one with no obvious cause.
  SIGNUP_VALIDATION_FAILED: 'signup_validation_failed',

  // Trial vs pay (PIX-557). The question this whole project started from:
  // at the last step, does the student take the trial, pay outright, or
  // leave? Today all three are indistinguishable from never arriving.
  SIGNUP_PLAN_CHOSEN: 'signup_plan_chosen',
  TRIAL_STARTED: 'trial_started',
  // A purchase that supersedes a running trial. Emitted at checkout
  // (PIX-560), not here - but named here because it is the sibling of
  // trial completion: a mid-trial buyer never reaches the expired state, so
  // without this event our best outcome would read as churn.
  TRIAL_CONVERTED: 'trial_converted',

  // Paywall (PIX-558). Two moments, not one - the gap between them is the
  // signal. Exposure is "they saw it was locked"; intent is "they tried to
  // get in". Only clicks loses the denominator; only renders can't tell a
  // student who scrolled past from one who wanted through.
  PAYWALL_ENCOUNTERED: 'paywall_encountered',
  PAYWALL_INTENT: 'paywall_intent',

  // Checkout (PIX-559). Shares the funnel tracker with signup so the two
  // flows cannot drift in what they call abandonment.
  CHECKOUT_STEP_VIEWED: 'checkout_step_viewed',
  CHECKOUT_COMPLETED: 'checkout_completed',
  CHECKOUT_ABANDONED: 'checkout_abandoned',
  CHECKOUT_CURRENCY_CHANGED: 'checkout_currency_changed',
  CHECKOUT_VOUCHER_APPLIED: 'checkout_voucher_applied',
  CHECKOUT_VOUCHER_REJECTED: 'checkout_voucher_rejected',
  CHECKOUT_BILLING_FIELD_COMPLETED: 'checkout_billing_field_completed',
  // Checkout refuses to proceed on a missing address, a missing Indian
  // state, or a malformed tax id - and today that refusal is invisible.
  // Prime suspect for silent drop-off: these are frictions students abandon
  // rather than debug.
  CHECKOUT_BILLING_VALIDATION_FAILED: 'checkout_billing_validation_failed',

  // Payment gateway (PIX-560). Both providers render inside our own page
  // rather than navigating away, so a student who gives up INSIDE the
  // gateway triggers no page-unload - these callbacks are the only signal
  // that it happened at all.
  CHECKOUT_ORDER_CREATED: 'checkout_order_created',
  CHECKOUT_ORDER_FAILED: 'checkout_order_failed',
  CHECKOUT_GATEWAY_SDK_FAILED: 'checkout_gateway_sdk_failed',
  CHECKOUT_GATEWAY_OPENED: 'checkout_gateway_opened',
  CHECKOUT_GATEWAY_DISMISSED: 'checkout_gateway_dismissed',
  CHECKOUT_GATEWAY_ERROR: 'checkout_gateway_error',
  // A real decline, distinct from the student closing the window. Without
  // this the two are indistinguishable and bank failures would be read as
  // reluctance to pay - pointing at a pricing fix for a payments problem.
  CHECKOUT_PAYMENT_FAILED: 'checkout_payment_failed',
  CHECKOUT_VERIFY_FAILED: 'checkout_verify_failed',
  SUBSCRIPTION_PURCHASED: 'subscription_purchased',

  // Study loop (PIX-561). QUESTION_ATTEMPTED is half the activation signal
  // - a student counts as activated on their first attempt OR first Sorty
  // query, whichever comes first (PIX-562 supplies the other half).
  QUESTION_VIEWED: 'question_viewed',
  QUESTION_ATTEMPTED: 'question_attempted',
  // "Sorted Solution" is a tab in the question view, alongside Question,
  // Mark Scheme and Your Marks - the AI-generated worked solution.
  SORTED_SOLUTION_VIEWED: 'sorted_solution_viewed',
  MARK_SCHEME_VIEWED: 'mark_scheme_viewed',
  // Flag Solution / "Report an issue" (PIX-711). One flag per question tab;
  // FLAGGED is a new one, UPDATED a real change, REMOVED a withdrawal.
  SOLUTION_FLAGGED: 'solution_flagged',
  SOLUTION_FLAG_UPDATED: 'solution_flag_updated',
  SOLUTION_FLAG_REMOVED: 'solution_flag_removed',
  // "Report sorty's behaviour" from any chat or live surface. Shape only:
  // never the comment or the captured transcript.
  SORTY_REPORTED: 'sorty_reported',

  // Sorty, the AI tutor (PIX-562). The other half of the activation signal.
  // Never carries message content, uploaded files, or response text - only
  // that an interaction happened and what shape it had.
  SORTY_QUERY_SENT: 'sorty_query_sent',
  SORTY_QUERY_FAILED: 'sorty_query_failed',
  SORTY_VOICE_STARTED: 'sorty_voice_started',
  SORTY_UPLOAD_STARTED: 'sorty_upload_started',
  SORTY_MARKING_REQUESTED: 'sorty_marking_requested',
  // Launcher onboarding hints (PIX-711): does nudging drive sorty usage?
  SORTY_HINT_SHOWN: 'sorty_hint_shown',
  SORTY_HINT_ACTIONED: 'sorty_hint_actioned',
  SORTY_HINT_DISMISSED: 'sorty_hint_dismissed',

  // Invited-staff onboarding (PIX-609). The seat-expansion loop: a school that
  // invites five teachers and activates one has a problem visible nowhere
  // else, and it is the problem most predictive of whether they renew.
  //
  // The invitation itself is the existing member_added event, emitted by the
  // INVITER. Everything below comes from the invitee's own browser, so the two
  // halves are joined in analysis on the organisation group - never by
  // threading a funnel session across two different people.
  ORG_STAFF_PASSWORD_SET: 'org_staff_password_set',
  // An invitation link that had already expired. Where invitations quietly
  // die, and indistinguishable today from a teacher who never opened the email.
  ORG_STAFF_INVITE_LINK_INVALID: 'org_staff_invite_link_invalid',
  // Every panel login. First login is then the first occurrence per person,
  // which needs no separate event and gives login frequency for free.
  ORG_PANEL_LOGGED_IN: 'org_panel_logged_in',

  // Worksheet builder (PIX-610). One funnel with a path breakdown rather than
  // three funnels: the question worth asking is comparative - which of the
  // three creation paths actually produces finished worksheets, and which are
  // opened and abandoned - and that needs one funnel you can break down.
  WORKSHEET_BUILDER_STEP_VIEWED: 'worksheet_builder_step_viewed',
  WORKSHEET_BUILDER_COMPLETED: 'worksheet_builder_completed',
  WORKSHEET_BUILDER_ABANDONED: 'worksheet_builder_abandoned',
  // A submission the server rejected. Without this a failed submit is
  // indistinguishable from the teacher losing interest: both end the attempt
  // as an abandonment, and the fixes they call for are opposite. Emitted
  // through the funnel tracker so it carries the builder path, which is what
  // makes "the upload path fails more often than the repository path"
  // answerable.
  WORKSHEET_BUILDER_SUBMIT_FAILED: 'worksheet_builder_submit_failed',

  // Worksheet builder, Sorty generation path (PIX-615).
  //
  // Client-side only, deliberately. Generation completes server-side and the
  // client learns by polling, so a teacher who closes the tab produces no
  // completion event - and the bias is not random, it favours fast
  // connections, small worksheets and patient people. Rather than emit a
  // completion we cannot trust, only the two reliable moments are recorded
  // (requested, and the worksheet eventually submitted) and the gap between
  // them is left as a measurable black box: the review stage's
  // seconds_on_previous_stage IS the generation wait.
  SORTY_WORKSHEET_REQUESTED: 'sorty_worksheet_requested',
  // A generation that never produced a worksheet. Separated from abandonment
  // because an AI failure and a teacher losing interest need opposite fixes.
  SORTY_WORKSHEET_FAILED: 'sorty_worksheet_failed',
  // Returning to a draft generated in an earlier session - the signal that a
  // long generation was recovered rather than abandoned.
  SORTY_WORKSHEET_RESUMED: 'sorty_worksheet_resumed',

  // Worksheet builder, upload path and the QR phone handoff (PIX-614).
  //
  // All three are emitted by the DESKTOP, never by the phone page. The phone
  // is a different browser and therefore a different analytics person, so
  // instrumenting that page would produce orphaned events from an anonymous
  // visitor that no funnel could join to the teacher who started the build.
  // The desktop already polls for the file, so it knows.
  //
  // All three go through the builder funnel's own tracker rather than the
  // facade, so they carry the builder path and the current stage: a
  // QR-issued-to-received rate is only interesting broken down by the path
  // and curriculum it happened on.
  WORKSHEET_UPLOAD_QR_ISSUED: 'worksheet_upload_qr_issued',
  WORKSHEET_UPLOAD_QR_RECEIVED: 'worksheet_upload_qr_received',
  // A QR that timed out with nothing uploaded - a teacher who reached for
  // their phone and gave up, which is a different failure from never trying.
  WORKSHEET_UPLOAD_QR_EXPIRED: 'worksheet_upload_qr_expired',

  // Organisation registration (PIX-608). Two stages, not four: the details
  // form and email verification. Completion coincides with automatic login
  // into the panel, so it is also where the activation clock starts.
  ORG_SIGNUP_STEP_VIEWED: 'org_signup_step_viewed',
  ORG_SIGNUP_COMPLETED: 'org_signup_completed',
  ORG_SIGNUP_ABANDONED: 'org_signup_abandoned',
  // A server-side rejection that keeps the registrant on the details form.
  // Invisible today, and the prime suspect for silent drop-off - this is
  // friction people abandon rather than debug.
  ORG_SIGNUP_VALIDATION_FAILED: 'org_signup_validation_failed',
  ORG_SIGNUP_VERIFICATION_RESENT: 'org_signup_verification_resent',
  // A wrong or expired code. Separated from abandonment because "the email
  // never arrived" and "they gave up" call for completely different fixes.
  ORG_SIGNUP_VERIFICATION_FAILED: 'org_signup_verification_failed',

  // Organisation pricing intent (PIX-606). Two moments, mirroring the student
  // paywall split: reaching the credits section is exposure, clicking through
  // to it from the nav is intent, and the gap between them is the signal.
  // These are the ONLY monetisation events available on this surface - the
  // credits product the landing page markets does not exist in the backend, so
  // there is no balance to gate on and no purchase to instrument.
  CREDITS_SECTION_VIEWED: 'credits_section_viewed',
  CREDITS_NAV_CLICKED: 'credits_nav_clicked',

  // Module actions (PIX-563/564). One event with a module property rather
  // than fifteen differently-shaped ones, because the question worth asking
  // is comparative: which modules turn a visit into an action, and which
  // are opened and abandoned.
  MODULE_ACTION: 'module_action',

  // Teacher webinar registration on /webinars. Fired only once the Google
  // Sheet has accepted the row, so the count matches the sheet.
  WEBINAR_REGISTRATION_SUBMITTED: 'webinar_registration_submitted',
});

export const PROPS = Object.freeze({
  // Route identity. ROUTE_PATH is the route *pattern* (`/upload/:token`),
  // never the resolved path - see redaction.js for why that matters.
  ROUTE_NAME: 'route_name',
  ROUTE_PATH: 'route_path',
  REQUIRES_AUTH: 'requires_auth',
  // Which audience's surface the event came from. The dimension that keeps
  // student and organisation funnels apart inside one shared project - see
  // surface.js for how it is resolved and why by path prefix.
  SURFACE: 'surface',
  // Top of the signup funnel. See LANDING_ROUTES for what counts and why.
  IS_LANDING: 'is_landing',
  // A landing visit by someone already signed in is a returning student
  // browsing the blog, not a prospect - counting them would inflate the
  // denominator of every acquisition metric.
  IS_AUTHENTICATED: 'is_authenticated',

  // Super properties - registered once, then attached to every event so any
  // funnel can be broken down without re-instrumenting anything.
  //
  // Country is deliberately absent: PostHog resolves it server-side from the
  // request via GeoIP, and we have no trustworthy client-side value to add.
  BOARD: 'board',
  BOARD_NAME: 'board_name',
  SUBSCRIPTION_STATUS: 'subscription_status',
  IS_TRIAL: 'is_trial',
  DAYS_LEFT_IN_TRIAL: 'days_left_in_trial',
  DAYS_SINCE_TRIAL_START: 'days_since_trial_start',
  HAS_BUNDLE: 'has_bundle',
  IS_ORGANISATION_USER: 'is_organisation_user',
  ORGANISATION_ROLE: 'organisation_role',
  USER_ROLE: 'user_role',

  // Panel role context (PIX-605). Deliberately NOT written into
  // IS_ORGANISATION_USER / ORGANISATION_ROLE above: those mean "this STUDENT
  // belongs to a school", and student insights already filter on them -
  // writing teacher values there would silently change what they count.
  ORG_PANEL_ROLE: 'org_panel_role',
  IS_ORG_OWNER: 'is_org_owner',
  ORGANISATION_COUNT: 'organisation_count',
  APP_ENVIRONMENT: 'app_environment',

  // Funnel context. Mirrors the keys funnelTracker.js emits - that module
  // stays taxonomy-free so it can be reused by any flow, so these are the
  // canonical spelling for querying rather than its source of truth.
  FUNNEL: 'funnel',
  FUNNEL_SESSION_ID: 'funnel_session_id',
  FUNNEL_STAGE: 'funnel_stage',
  // Stage events report the duration of the stage just LEFT - on arrival the
  // new one is always zero seconds old. Terminal events report the stage the
  // student gave up or finished on.
  PREVIOUS_STAGE: 'previous_stage',
  SECONDS_ON_PREVIOUS_STAGE: 'seconds_on_previous_stage',
  SECONDS_ON_STAGE: 'seconds_on_stage',
  SECONDS_IN_FUNNEL: 'seconds_in_funnel',
  ABANDON_REASON: 'abandon_reason',

  // Signup
  SIGNUP_METHOD: 'signup_method',
  SIGNUP_STEP: 'signup_step',
  // Which category of field the server rejected - never the value itself.
  VALIDATION_FIELDS: 'validation_fields',

  // Self-reported "how did you hear about us", which cross-checks the
  // automatic attribution and catches offline channels it cannot see.
  REFERRAL_SOURCE: 'referral_source',

  // Trial / plan choice
  PLAN_CHOICE: 'plan_choice',
  TRIAL_START_PATH: 'trial_start_path',
  BOARD_SELECTION: 'board_selection',
  IS_BUNDLE: 'is_bundle',

  // Paywall
  PAYWALL_MODULE: 'paywall_module',
  PAYWALL_SOURCE: 'paywall_source',

  // Checkout
  CHECKOUT_ENTRY: 'checkout_entry',
  BILLING_CYCLE: 'billing_cycle',
  INTERVAL_MONTHS: 'interval_months',
  BILLED_TODAY: 'billed_today',
  CURRENCY: 'currency',
  // Which billing field category was completed or rejected - never its
  // value. Address and tax identifiers must not leave the browser.
  BILLING_FIELD: 'billing_field',
  VOUCHER_REJECT_REASON: 'voucher_reject_reason',
  HAS_VOUCHER: 'has_voucher',

  // Payment
  GATEWAY: 'gateway',
  AMOUNT: 'amount',
  FAILURE_REASON: 'failure_reason',
  // Whether this purchase superseded a running trial, and on which day of
  // it. Together these are what make "bought on day 3 vs day 9" and
  // mid-trial versus post-trial conversion answerable at all.
  SUPERSEDED_TRIAL: 'superseded_trial',

  // Study
  SUBJECT: 'subject',
  SUBJECT_NAME: 'subject_name',
  TOPIC: 'topic',
  QUESTION_UUID: 'question_uuid',
  // Which surface the student came from - past papers, topic-wise,
  // recommended, exam builder, worksheet. Same question, different intent.
  STUDY_CONTEXT: 'study_context',
  ATTEMPT_OUTCOME: 'attempt_outcome',
  MARKS_AWARDED: 'marks_awarded',
  TOTAL_MARKS: 'total_marks',

  // Sorty
  SORTY_SURFACE: 'sorty_surface',
  // A slow tutor is a plausible cause of abandonment, and the number costs
  // nothing to record while the request is already being awaited.
  RESPONSE_MS: 'response_ms',
  HAS_SELECTED_TEXT: 'has_selected_text',
  // Question-view tab an event happened on (question, mark_scheme, ...).
  TAB: 'tab',
  HINT_VARIANT: 'hint_variant',
  // voice | type | launcher | selection
  HINT_ACTION: 'hint_action',
  // close | auto
  DISMISS_METHOD: 'dismiss_method',
  // Flags carry shape only - never the comment text.
  ENTRY_POINT: 'entry_point',
  FLAG_REASON: 'flag_reason',
  HAS_COMMENT: 'has_comment',
  FLAG_SOURCE: 'flag_source',
  // Which sorty surface a report came from (question_chat, sorty_ai_voice,
  // ...). Not `surface`: that is the student/org super-property.
  REPORT_SURFACE: 'report_surface',
  HAS_ATTACHMENTS: 'has_attachments',
  FILE_COUNT: 'file_count',

  // Module actions
  MODULE: 'module',
  MODULE_ACTION: 'module_action',
  ITEM_UUID: 'item_uuid',
  RESULT_COUNT: 'result_count',
  FAILURE_STAGE: 'failure_stage',
  UPLOAD_CHANNEL: 'upload_channel',
  MEMBER_ROLE: 'member_role',
  // The role an invited person was invited AS, read from the set-password
  // token check (PIX-626). Needed because invited staff and invited students
  // are sent through the same unauthenticated set-password route, so without
  // it the invited-staff funnel silently counts students too. Distinct from
  // ORG_PANEL_ROLE, which describes an authenticated panel session.
  INVITED_ROLE: 'invited_role',
  // Which reporting surface inside a panel module. Needed because module
  // actions carry no route name - only pageviews do - and the classroom and
  // insights modules each have several screens worth telling apart.
  PANEL_SCREEN: 'panel_screen',
  // How a student reached a classroom. Counting only direct adds would report
  // a school that populated its classroom by approving join requests as never
  // having activated at all.
  ARRIVAL_PATH: 'arrival_path',
  // How many boards and subjects a staff member was granted. A school with
  // teachers but no board assignments looks identical to an inactive one
  // otherwise - nothing else works until these are set.
  BOARD_COUNT: 'board_count',
  SUBJECT_COUNT: 'subject_count',

  // Worksheet builder. Which of the three creation paths produced this event -
  // the question repository, an uploaded paper, or Sorty generation.
  BUILDER_PATH: 'builder_path',
  // How many students a finished worksheet went to, INCLUDING zero: building a
  // worksheet and publishing one to a class are different outcomes, and a
  // teacher who builds but never assigns is the more interesting of the two.
  ASSIGNED_STUDENT_COUNT: 'assigned_student_count',
  QUESTION_COUNT: 'question_count',
  // Which half of an uploaded worksheet this concerns - the question paper or
  // the mark scheme. Teachers skip the second far more often than the first.
  UPLOAD_SLOT: 'upload_slot',

  // Organisation registration. Whether the registrant already held a
  // SortMyPrep account - the form's live email check already knows, and a cold
  // prospect converts differently enough from an existing student registering
  // a school that averaging the two describes neither.
  IS_EXISTING_ACCOUNT: 'is_existing_account',

  // Organisation pricing intent
  NAV_SOURCE: 'nav_source',

  // Webinar registration. Never the teacher's name, email or phone. The board
  // gets its own key because BOARD above is a super property meaning the
  // signed-in student's board; reusing it would mix the two in breakdowns.
  WEBINAR_TOPIC: 'webinar_topic',
  WEBINAR_DATE: 'webinar_date',
  WEBINAR_BOARD: 'webinar_board',
  // ISO country of the phone's dial code ("US"), not the number itself.
  PHONE_COUNTRY: 'phone_country',
});

/** Where a deliberate jump to the credits section came from. */
export const CREDITS_NAV_SOURCE = Object.freeze({
  HEADER: 'header',
  FOOTER: 'footer',
});

/**
 * DOM id of the credits section on the teacher landing page.
 *
 * Lives here, unusually for a DOM concern, because three places have to agree
 * on it: the section itself, and the header and footer links that jump to it.
 * If they drift, the nav event fires for a section that no longer scrolls into
 * view and the exposure event never fires at all - and nothing would throw.
 */
export const CREDITS_SECTION_ID = 'teacher-pricing';

/** Product modules, as they appear in analytics breakdowns. */
export const MODULES = Object.freeze({
  PAST_PAPERS: 'past_papers',
  TOPIC_WISE: 'topic_wise',
  RECOMMENDED: 'recommended',
  EXAM_BUILDER: 'exam_builder',
  PREDICTED_PAPERS: 'predicted_papers',
  MY_WORKSHEETS: 'my_worksheets',
  SAVED_QUESTIONS: 'saved_questions',
  INSIGHTS: 'insights',
  LEADERBOARD: 'leaderboard',
  PROFILE: 'profile',
  ORGANISATION: 'organisation',
  CLASSROOM: 'classroom',
  // The panel's reporting screens, kept separate from the student INSIGHTS
  // module above even though both are called "insights". They are different
  // features in different products that happen to share a name. INSIGHTS is
  // declared but not yet emitted anywhere on the student side, so sharing it
  // would not have mixed anything today - it would have mixed the moment the
  // student module was instrumented, in any breakdown that forgot a surface
  // filter, and that is a trap not worth leaving set.
  ORG_INSIGHTS: 'org_insights',
  WORKSHEET_BUILDER: 'worksheet_builder',
  GRADE_FOR_ME: 'grade_for_me',
});

/** What a student did inside a module. */
export const MODULE_ACTIONS = Object.freeze({
  FILTERED: 'filtered',
  // Served vs opened is what makes recommender click-through derivable -
  // a recommendation nobody opens is the signal that matters.
  SERVED: 'served',
  OPENED: 'opened',
  CREATED: 'created',
  STARTED: 'started',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
  UPLOAD_STARTED: 'upload_started',
  UPLOAD_SUCCEEDED: 'upload_succeeded',
  // A failed upload is a broken promise a student is unlikely to retry.
  UPLOAD_FAILED: 'upload_failed',
  FAILED: 'failed',
  BOARD_SWITCHED: 'board_switched',
  MEMBER_ADDED: 'member_added',
  MEMBER_EDITED: 'member_edited',
  // A student arriving in a classroom. Tier one of activation: a school with
  // no students structurally cannot reach a value action.
  STUDENT_ADDED: 'student_added',
  // An invitation SENT to a student, which is an intent, not an arrival.
  // Deliberately a separate action rather than a third arrival path: as a
  // path value it was summed together with the real arrivals by anyone
  // counting student_added, overstating classroom growth by every invitation
  // that was never accepted. Acceptance happens in the student's own browser
  // and is not observable from the panel at all.
  STUDENT_INVITED: 'student_invited',
  // A parent performance report actually sent. The feature exists for parents,
  // and whether teachers use it is entirely unknown today.
  EMAILED: 'emailed',
});

/**
 * How a student came to be in a classroom (PIX-612).
 *
 * Both values are genuine arrivals, so `student_added` can be counted without
 * a filter and the paths sum to the total. An invitation being sent is NOT
 * here - it is its own action, `STUDENT_INVITED`, because it is an intent
 * whose outcome the panel never sees.
 */
export const STUDENT_ARRIVAL_PATHS = Object.freeze({
  DIRECT: 'direct',
  JOIN_REQUEST_APPROVED: 'join_request_approved',
});

/**
 * Reporting screens inside the panel (PIX-611).
 *
 * These emit a module action even though they are only *views*, which the
 * module-action doctrine normally leaves to pageviews. The reason is context: a
 * pageview carries the route pattern, not the board and subject being looked
 * at, and "which curricula do teachers actually report on" is unanswerable
 * without it. The action is the carrier for that context, not a duplicate.
 */
export const PANEL_SCREENS = Object.freeze({
  CLASSROOM: 'classroom',
  STUDENT_DETAIL: 'student_detail',
  STUDENT_INSIGHTS: 'student_insights',
  SUBJECT_INSIGHTS: 'subject_insights',
  GRADING_REVIEW: 'grading_review',
});

/** Where a Sorty interaction originated. */
export const SORTY_SURFACE = Object.freeze({
  // Asking from inside a question is a different behaviour from opening the
  // standalone tutor - pooling them hides which entry point actually works.
  IN_QUESTION: 'in_question',
  STANDALONE: 'standalone',
  VOICE: 'voice',
  UPLOAD: 'upload',
});

/** Where a student ran into the paywall. */
export const PAYWALL_SOURCE = Object.freeze({
  SIDEBAR: 'sidebar',
  // Typing or bookmarking a Pro URL directly - a stronger intent signal
  // than clicking a locked item in a menu, and worth separating.
  DIRECT_URL: 'direct_url',
  SOLUTION: 'solution',
  DASHBOARD: 'dashboard',
  // Locked cards on the standalone sorty AI page (e.g. screen share).
  SORTY_AI: 'sorty_ai',
});

/** What the student picked at the last step of the wizard. */
export const PLAN_CHOICE = Object.freeze({
  TRIAL: 'trial',
  PAY_NOW: 'paynow',
});

/**
 * How a trial began. The two populations behave differently and only one of
 * them has a landing visit in front of it, so averaging them would describe
 * neither.
 */
export const TRIAL_START_PATH = Object.freeze({
  SIGNUP: 'signup',
  LEGACY_GATE: 'legacy_gate',
});

/** Named funnels, so stage events from different flows stay separable. */
export const FUNNELS = Object.freeze({
  SIGNUP: 'signup',
  CHECKOUT: 'checkout',
  ORG_SIGNUP: 'org_signup',
  WORKSHEET_BUILDER: 'worksheet_builder',
});

/** The three ways a teacher can create a worksheet. */
export const WORKSHEET_BUILDER_PATHS = Object.freeze({
  REPOSITORY: 'repository',
  UPLOAD: 'upload',
  SORTY: 'sorty',
});

/**
 * Worksheet builder stages - the UNION across all three creation paths, which
 * do not share a sequence. Each path traverses its own subset, and that is
 * fine because the stages are named rather than numbered: a per-path funnel is
 * a filter on `builder_path`, not a separate instrumentation.
 */
export const WORKSHEET_BUILDER_STAGES = Object.freeze({
  // In the builder, before a subject narrows the question bank. All paths.
  CONFIGURE: 'configure',
  // Content assembled: questions picked from the repository, or a paper
  // chosen for upload.
  QUESTIONS: 'questions',
  // Sorty only - the asynchronous generation the teacher waits through, and
  // the one stage that can outlive the tab.
  GENERATING: 'generating',
  // Reviewing and editing the sheet, including the printable preview.
  REVIEW: 'review',
  // Choosing which students receive it.
  ASSIGN: 'assign',
});

/**
 * Organisation registration stages, in the order a registrant meets them.
 * Named rather than numbered, like the student wizard's, so reordering the UI
 * does not silently rewrite history in existing funnels.
 */
export const ORG_SIGNUP_STAGES = Object.freeze({
  DETAILS: 'details',
  VERIFY: 'verify',
});

/** Checkout stages, in the order a student meets them. */
export const CHECKOUT_STAGES = Object.freeze({
  ENTERED: 'entered',
  PLAN_SELECTED: 'plan_selected',
  CYCLE_SELECTED: 'cycle_selected',
  PRICE_SHOWN: 'price_shown',
  BILLING: 'billing',
  SUMMARY_OPENED: 'summary_opened',
  GATEWAY_OPENED: 'gateway_opened',
});

/** How the student arrived at checkout. */
export const CHECKOUT_ENTRY = Object.freeze({
  SIGNUP: 'signup',
  STANDALONE: 'standalone',
  DASHBOARD: 'dashboard',
});

/**
 * Routes that count as a landing visit - the top of the signup funnel.
 *
 * Student marketing surfaces only. Two deliberate exclusions:
 *
 * `teacher-landing` leads to organisation registration, which never
 * provisions a student trial. Counting it would pad the denominator with
 * traffic that structurally cannot convert, making conversion look worse
 * than it is for reasons no product change could fix.
 *
 * The legal pages are reached mostly from inside the app by existing
 * students, so they measure nothing about acquisition.
 */
export const LANDING_ROUTES = Object.freeze([
  'home-lp',
  'blogs',
  'partners-lp',
  'webinars',
]);

/** Attribution carried in the URL or browser storage. */
export const ATTRIBUTION_PROPS = Object.freeze({
  REFERRAL_CODE: 'referral_code',
  PARTNERSHIP_CODE: 'partnership_code',
  REFERRAL_SOURCE: 'referral_source',
});

/** How the student is signing up. The Google path skips verification. */
export const SIGNUP_METHOD = Object.freeze({
  EMAIL: 'email',
  GOOGLE: 'google',
});

/** Wizard stages, in order. Named rather than numbered so a reordering of
 *  the UI doesn't silently rewrite history in existing funnels. */
export const SIGNUP_STAGES = Object.freeze({
  DETAILS: 'details',
  BOARD: 'board',
  PREVIEW: 'preview',
  TERMS: 'terms',
  VERIFY: 'verify',
});

/**
 * The two audiences this one build serves. An enum rather than free strings so
 * a breakdown can never fragment into `org`/`organisation`/`Organisation` for
 * the same surface - the failure this whole taxonomy exists to prevent.
 */
export const SURFACES = Object.freeze({
  STUDENT: 'student',
  ORGANISATION: 'organisation',
});

/**
 * Roles that grant organisation-panel access. Lowercased, matching how the
 * store exposes them, so a breakdown never splits `Admin`/`ADMIN`/`admin`.
 */
export const ORG_PANEL_ROLES = Object.freeze({
  ADMIN: 'admin',
  TEACHER: 'teacher',
});

/** Group types for group analytics (per-school rollups). */
export const GROUPS = Object.freeze({
  ORGANISATION: 'organisation',
});

/**
 * Subscription status values. Kept as an enum rather than free strings so a
 * breakdown never fragments into `free`/`Free`/`none` for the same state.
 */
export const SUBSCRIPTION_STATUS = Object.freeze({
  ANONYMOUS: 'anonymous',
  TRIAL: 'trial',
  PAID: 'paid',
  FREE: 'free',
});
